import React, { useState, useEffect, useCallback } from 'react';
import { _T, C } from '../../shared/theme';

const BASE = process.env.REACT_APP_API_URL || '/api';

const PACKS = [
  { id: 'credits_100',   label: '100 Credits',    price: '€1.99',  credits: 100,   popular: false },
  { id: 'credits_500',   label: '500 Credits',    price: '€7.49',  credits: 500,   popular: true  },
  { id: 'credits_2000',  label: '2,000 Credits',  price: '€24.99', credits: 2000,  popular: false },
  { id: 'credits_10000', label: '10,000 Credits', price: '€89.99', credits: 10000, popular: false },
];

const REASON_LABELS = {
  monthly_grant:    { icon: '🎁', label: 'Monthly grant' },
  activation_grant: { icon: '🚀', label: 'Welcome bonus' },
  credit_purchase:  { icon: '💳', label: 'Purchase' },
  admin_grant:      { icon: '⭐', label: 'Admin grant' },
  game:             { icon: '🏋️', label: 'Game' },
  exercise:         { icon: '🧘', label: 'Exercise' },
  ai:               { icon: '🤖', label: 'AI assist' },
  coach_gift:       { icon: '🎓', label: 'Coach gift' },
  coach_gift_out:   { icon: '🎁', label: 'Gift sent' },
  spend:            { icon: '⚡', label: 'Used' },
};

function fmt(reason) {
  return REASON_LABELS[reason] || { icon: '💠', label: reason };
}

function formatDate(str) {
  try {
    const d = new Date(str);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return str; }
}

export default function WalletScreen({
  lang, currentUserId,
  walletBalance, setWalletBalance,
  businessId, bizWalletBalance, setBizWalletBalance,
}) {
  const nl = lang === 'nl';
  const isBiz = !!businessId;
  const [txns, setTxns]       = useState(null);
  const [buying, setBuying]   = useState(null);
  const [error, setError]     = useState(null);
  const [balance, setBalance] = useState(isBiz ? (bizWalletBalance ?? 0) : (walletBalance ?? 0));

  // Keep local balance in sync with parent state
  useEffect(() => {
    setBalance(isBiz ? (bizWalletBalance ?? 0) : (walletBalance ?? 0));
  }, [isBiz, bizWalletBalance, walletBalance]);

  const load = useCallback(() => {
    const walletId = isBiz
      ? encodeURIComponent('biz_' + businessId)
      : encodeURIComponent(currentUserId || localStorage.getItem('sl_user_id') || '');
    if (!walletId) return;
    fetch(`${BASE}/wallet/${walletId}`)
      .then(r => r.json())
      .then(data => {
        if (data.balance !== undefined) {
          setBalance(data.balance);
          // Update parent state — no localStorage writes
          if (isBiz) { if (setBizWalletBalance) setBizWalletBalance(data.balance); }
          else        { if (setWalletBalance)    setWalletBalance(data.balance); }
        }
        setTxns(data.transactions || []);
      })
      .catch(() => setTxns([]));
  }, [isBiz, businessId, currentUserId, setWalletBalance, setBizWalletBalance]);

  useEffect(() => { load(); }, [load]);

  const buyPack = async (packId) => {
    const uid = currentUserId || localStorage.getItem('sl_user_id');
    if (!uid) return;
    setBuying(packId);
    setError(null);
    try {
      const res = await fetch(`${BASE}/stripe/credits-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, packId }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || (nl ? 'Betaling mislukt.' : 'Payment failed.'));
      }
    } catch {
      setError(nl ? 'Verbindingsfout. Probeer opnieuw.' : 'Connection error. Please try again.');
    } finally {
      setBuying(null);
    }
  };

  const accent = _T.accent;

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', paddingBottom: 32 }}>
      {/* Balance card */}
      <div style={{
        background: `linear-gradient(135deg, ${accent}22, ${_T.grad2}22)`,
        border: `1px solid ${accent}44`,
        borderRadius: 20,
        padding: '28px 24px',
        textAlign: 'center',
        marginBottom: 24,
      }}>
        <div style={{ fontSize: 11, color: accent, letterSpacing: 3, fontWeight: 700, marginBottom: 8 }}>
          {isBiz ? (nl ? 'TEAM CREDITS' : 'TEAM CREDITS') : (nl ? 'MIJN CREDITS' : 'MY CREDITS')}
        </div>
        <div style={{ fontSize: 52, fontWeight: 800, color: '#fff', lineHeight: 1, letterSpacing: -1 }}>
          {balance.toLocaleString()}
        </div>
        <div style={{ fontSize: 13, color: C.silver, marginTop: 6 }}>
          {isBiz
            ? (nl ? 'beschikbare team credits' : 'available team credits')
            : (nl ? 'beschikbare credits' : 'available credits')}
        </div>
      </div>

      {/* Buy credits — only for personal (non-business) wallets */}
      {!isBiz && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, color: C.silver, letterSpacing: 2, fontWeight: 700, marginBottom: 14 }}>
            {nl ? 'CREDITS KOPEN' : 'BUY CREDITS'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {PACKS.map(pack => (
              <button
                key={pack.id}
                onClick={() => buyPack(pack.id)}
                disabled={!!buying}
                style={{
                  position: 'relative',
                  background: pack.popular
                    ? `linear-gradient(135deg, ${accent}33, ${_T.grad2}33)`
                    : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${pack.popular ? accent + '88' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: 16,
                  padding: '16px 12px',
                  cursor: buying ? 'not-allowed' : 'pointer',
                  opacity: buying && buying !== pack.id ? 0.6 : 1,
                  textAlign: 'center',
                  transition: 'opacity 0.15s',
                }}
              >
                {pack.popular && (
                  <div style={{
                    position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)',
                    background: accent, color: '#0a1220', fontSize: 9, fontWeight: 800,
                    letterSpacing: 1.5, padding: '2px 10px', borderRadius: 20,
                  }}>
                    {nl ? 'POPULAIR' : 'POPULAR'}
                  </div>
                )}
                <div style={{ fontSize: 13, fontWeight: 700, color: pack.popular ? accent : '#fff', marginBottom: 4 }}>
                  {pack.label}
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>
                  {buying === pack.id ? '...' : pack.price}
                </div>
              </button>
            ))}
          </div>
          {error && (
            <div style={{ marginTop: 12, color: '#C05050', fontSize: 12, textAlign: 'center' }}>{error}</div>
          )}
        </div>
      )}

      {/* Business wallet notice */}
      {isBiz && (
        <div style={{
          background: `${accent}10`,
          border: `1px solid ${accent}33`,
          borderRadius: 14,
          padding: '14px 16px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <span style={{ fontSize: 22 }}>🏢</span>
          <div style={{ fontSize: 13, color: C.silver, lineHeight: 1.5 }}>
            {nl
              ? 'Credits worden beheerd via je bedrijfsaccount. Neem contact op met je beheerder om credits toe te voegen.'
              : 'Credits are managed through your business account. Contact your admin to top up credits.'}
          </div>
        </div>
      )}

      {/* How credits work */}
      <div style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: '16px 18px',
        marginBottom: 28,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.silver, marginBottom: 10 }}>
          {nl ? 'Hoe werken credits?' : 'How do credits work?'}
        </div>
        {[
          nl
            ? ['🏋️', 'Gebruik credits voor oefeningen & games']
            : ['🏋️', 'Use credits for exercises & games'],
          nl
            ? ['🤖', 'AI-analyse kost 1–5 credits']
            : ['🤖', 'AI analysis costs 1–5 credits'],
          nl
            ? ['🎁', isBiz ? 'Credits beheerd door jouw organisatie' : 'Maandelijkse credits inbegrepen bij jouw plan']
            : ['🎁', isBiz ? 'Credits managed by your organisation' : 'Monthly credits included with your plan'],
        ].map(([icon, text], i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: i < 2 ? 8 : 0 }}>
            <span style={{ fontSize: 16 }}>{icon}</span>
            <span style={{ fontSize: 12, color: '#aac0d8', lineHeight: 1.4 }}>{text}</span>
          </div>
        ))}
      </div>

      {/* Transaction history */}
      <div>
        <div style={{ fontSize: 11, color: C.silver, letterSpacing: 2, fontWeight: 700, marginBottom: 14 }}>
          {nl ? 'TRANSACTIES' : 'TRANSACTIONS'}
        </div>
        {txns === null ? (
          <div style={{ color: C.silver, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
            {nl ? 'Laden...' : 'Loading...'}
          </div>
        ) : txns.length === 0 ? (
          <div style={{ color: C.silver, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
            {nl ? 'Nog geen transacties.' : 'No transactions yet.'}
          </div>
        ) : (
          txns.map((tx, i) => {
            const { icon, label } = fmt(tx.reason);
            const isPos = tx.amount > 0;
            return (
              <div
                key={i}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 0',
                  borderBottom: i < txns.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: isPos ? 'rgba(74,200,122,0.12)' : 'rgba(255,100,100,0.1)',
                  border: `1px solid ${isPos ? '#4AC87A33' : '#C0505033'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18,
                }}>
                  {icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{label}</div>
                  {tx.ref_id && (
                    <div style={{ color: C.silver, fontSize: 11, marginTop: 1, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                      {tx.ref_id}
                    </div>
                  )}
                  <div style={{ color: '#4a6080', fontSize: 11, marginTop: 1 }}>{formatDate(tx.created_at)}</div>
                </div>
                <div style={{
                  fontSize: 15, fontWeight: 700, flexShrink: 0,
                  color: isPos ? '#4AC87A' : '#FF6464',
                }}>
                  {isPos ? '+' : ''}{tx.amount.toLocaleString()}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
